import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { IDENTIFIER_SYSTEMS } from './identifiers';
import { resolveObservationResultCode } from './resultConcepts';
import { isResultCodeSystem } from './taskUtils';

export type ObservationRecord = WithId<Observation>;

const LOINC_SYSTEM = 'http://loinc.org';

/**
 * Health Gorilla wraps each lab result in a profiled "group" Observation that carries no result of
 * its own — it only nests the real leaf via `hasMember[]`. See the lab-orders design doc.
 */
export const HG_OBSERVATION_GROUP_PROFILE = 'https://healthgorilla.com/fhir/StructureDefinition/hg-observaton-group';

/**
 * One measurement's readings. Unlike the immunization, allergy and family-history groupers, results
 * never collapse: every reading is a point on a chart, so a group has no displayed-record /
 * `merged[]` split and no same-day rule. Grouping only decides which row a reading lands on.
 */
export type ResultGroup = {
  /** The resolved code the row is keyed and labelled by — a host's expected code where one matched. */
  key: string;
  /** Newest first. */
  records: ObservationRecord[];
  /** The newest reading — the one a summary row shows. */
  latest: ObservationRecord;
  /** `effectiveDateTime` of `latest`, or undefined when it carries none. */
  mostRecent: string | undefined;
};

export type GroupedResults = {
  groups: ResultGroup[];
  /** Administrative rows an exporter files as observations (a narrative, a dispense status). */
  hidden: ObservationRecord[];
};

/** Whether the reading was ingested from an outside source rather than authored in Liza. */
export const isImportedObservation = (record: Observation): boolean =>
  (record.identifier ?? []).some((id) => id.system === IDENTIFIER_SYSTEMS.EXTERNAL_ORIGINAL_ID);

/** Whether a reading should be excluded from a result list (administrative/metadata observations). */
export const shouldExcludeObservation = (name: string): boolean => {
  const nameLower = name.toLowerCase();

  const excludedExactNames = ['narrative', 'text', 'note'];
  if (excludedExactNames.includes(nameLower)) {
    return true;
  }

  const excludedPatterns = [
    'expiration date',
    'expiry date',
    'dispense status',
    'coding status',
    'coding system',
    'product code',
    'product blood type',
    'blood expiration',
    'dispense',
    'unit number',
    'conversion',
    'equation',
    'calculation',
  ];

  if (excludedPatterns.some((pattern) => nameLower.includes(pattern))) {
    return true;
  }

  // Exact match patterns for generic single-word names
  const exactMatchPatterns = ['status', 'code', 'type'];
  return exactMatchPatterns.includes(nameLower);
};

/**
 * A stable key for grouping readings. When `preferred` is supplied (the codes a view expects — a care
 * plan's measurement templates, say), a reading carrying one of those codes keys to it, and a reading
 * carrying a sibling of one keys to the sibling, so variants of one measurement land on one row.
 * Otherwise it falls back to the first LOINC code, then the first code, then the formatted name.
 */
export const getObservationIdentifier = (observation: Observation, preferred?: ReadonlySet<string>): string => {
  if (preferred?.size) {
    const resolved = resolveObservationResultCode(observation, preferred);
    if (resolved) return resolved;
  }
  const codings = observation.code?.coding ?? [];
  return (
    codings.find((c) => c.system === LOINC_SYSTEM)?.code ||
    codings.at(0)?.code ||
    formatCodeableConcept(observation.code) ||
    ''
  );
};

const instant = (record: Observation): number => {
  const ms = Date.parse(record.effectiveDateTime ?? '');
  return Number.isNaN(ms) ? 0 : ms;
};

/**
 * Group readings by measurement, newest first within a group and by newest reading across groups.
 *
 * Naming is left to the host: a row is labelled from its `key`, not from whichever reading happens to
 * be newest, so a merged row's label does not flip when a variant-coded reading arrives (the same
 * split the immunization grouper makes with `useImmunizationGroupLabel`).
 */
export const groupResultRecords = (
  records: readonly ObservationRecord[],
  preferred?: ReadonlySet<string>,
): GroupedResults => {
  const byKey = new Map<string, ObservationRecord[]>();
  const hidden: ObservationRecord[] = [];

  for (const record of records) {
    const name = formatCodeableConcept(record.code) ?? '';
    if (name && shouldExcludeObservation(name)) {
      hidden.push(record);
      continue;
    }
    const key = getObservationIdentifier(record, preferred);
    if (!key) {
      hidden.push(record);
      continue;
    }
    const existing = byKey.get(key);
    if (existing) existing.push(record);
    else byKey.set(key, [record]);
  }

  const groups = [...byKey.entries()].flatMap(([key, group]) => {
    const sorted = [...group].sort((a, b) => instant(b) - instant(a));
    const latest = sorted[0];
    if (!latest) return [];
    return [{ key, records: sorted, latest, mostRecent: latest.effectiveDateTime }];
  });
  groups.sort((a, b) => instant(b.latest) - instant(a.latest));

  return { groups, hidden };
};

/** The LOINC/survey codes a reading carries at its top level. */
export const observationResultCodes = (observation: Observation): string[] =>
  (observation.code?.coding ?? []).flatMap((coding) =>
    isResultCodeSystem(coding.system) && coding.code ? [coding.code] : [],
  );
